import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";
import { useState } from "react";
import { Badge, Button, DataTable, Dialog, EmptyState, formatDateTime, formatRelative, KeyValue, Notice, PageHeader, Pagination, Panel, Select, type BadgeTone } from "@hostdock/ui";
import type { AdminSecurityFinding, FindingVerdict } from "@hostdock/types";
import { api } from "../api";
import { RequirePermission } from "../lib/guard";
import { useListParams } from "../lib/list-params";

const PAGE_SIZE = 50;
const VERDICT: Record<FindingVerdict, { label: string; tone: BadgeTone }> = {
  MALICIOUS: { label: "Malware", tone: "danger" },
  LIKELY_MALICIOUS: { label: "Likely malicious", tone: "danger" },
  SUSPICIOUS: { label: "Suspicious", tone: "warning" },
};
const lower = (s: string) => s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, " ");

export function SecurityPage() {
  return <RequirePermission anyOf={["WEBSITE_VIEW"]}><Security /></RequirePermission>;
}

/** Open malware findings across every customer's latest scan. Read-only: customers review and quarantine their files. */
function Security() {
  const { values, page, set } = useListParams(["verdict", "status"] as const);
  const [open, setOpen] = useState<AdminSecurityFinding | null>(null);
  const q = useQuery({
    queryKey: ["admin-security", values, page],
    queryFn: () => api.security.findings({ ...values, status: values.status || "OPEN", page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
  const s = q.data?.summary;
  return (
    <>
      <PageHeader title="Malware scans" description="Files flagged by each website's latest malware scan (ClamAV + DockShield). Customers review, quarantine and restore their own files; nothing is removed automatically." />
      {s && (
        <Notice tone={s.malicious + s.likelyMalicious ? "danger" : s.suspicious ? "warning" : "success"}>
          {s.websites
            ? `${s.malicious} malware, ${s.likelyMalicious} likely malicious and ${s.suspicious} suspicious open finding${s.malicious + s.likelyMalicious + s.suspicious === 1 ? "" : "s"} on ${s.websites} website${s.websites === 1 ? "" : "s"}.`
            : "No open findings in any website's latest scan."}
        </Notice>
      )}
      <Panel flush>
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
          <Select value={values.verdict} onChange={(e) => set({ verdict: e.target.value })} className="w-48" aria-label="Verdict">
            <option value="">All verdicts</option>
            {(Object.keys(VERDICT) as FindingVerdict[]).map((v) => <option key={v} value={v}>{VERDICT[v].label}</option>)}
          </Select>
          <Select value={values.status || "OPEN"} onChange={(e) => set({ status: e.target.value })} className="w-48" aria-label="Review status">
            <option value="OPEN">Needs action</option>
            <option value="FALSE_POSITIVE">Marked false positive</option>
            <option value="RESOLVED">Resolved / quarantined</option>
          </Select>
        </div>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(f) => f.id}
          onRowClick={setOpen}
          empty={<EmptyState icon={ShieldAlert} title="No findings" description="Nothing matches in the latest scans. Websites are scanned weekly by default, or when customers click Scan now." />}
          columns={[
            { key: "v", header: "Verdict", cell: (f) => <Badge tone={VERDICT[f.verdict].tone} dot>{VERDICT[f.verdict].label}</Badge> },
            { key: "w", header: "Website", cell: (f) => <div><p className="font-medium">{f.website.primaryDomain}</p><p className="text-[12px] text-muted">{f.customer.name}</p></div> },
            { key: "p", header: "File", cell: (f) => <span className="font-mono text-[12px] break-all">{f.path}</span> },
            { key: "s", header: "Severity", cell: (f) => <span className="text-muted">{lower(f.severity)}</span>, hideOnMobile: true },
            { key: "t", header: "Found", cell: (f) => <span className="text-muted" title={formatDateTime(f.createdAt)}>{formatRelative(f.createdAt)}</span>, hideOnMobile: true },
          ]}
        />
        {q.data && <Pagination page={page} pageSize={PAGE_SIZE} total={q.data.total} onPage={(p) => set({ page: String(p) })} />}
      </Panel>
      {open && <FindingDialog f={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function FindingDialog({ f, onClose }: { f: AdminSecurityFinding; onClose(): void }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={f.title} description={`${f.website.primaryDomain} · ${f.path}`} size="lg" footer={<Button onClick={onClose}>Close</Button>}>
      <div className="flex flex-col gap-4">
        <KeyValue items={[
          { label: "Verdict", value: VERDICT[f.verdict].label }, { label: "Severity", value: lower(f.severity) }, { label: "Confidence", value: lower(f.confidence) },
          { label: "Review", value: lower(f.status) }, { label: "Customer", value: f.customer.name }, { label: "SHA-256", value: f.sha256 ?? "—" },
        ]} />
        <p className="text-[13px] text-muted">{f.explanation}</p>
        <ul className="divide-y divide-line rounded-md border border-line text-[13px]">
          {f.evidence.map((e, i) => (
            <li key={i} className="px-3 py-2">
              <p className="font-medium">{e.engine === "clamav" ? "ClamAV" : "DockShield"} · {e.ruleId}{e.ruleVersion ? ` v${e.ruleVersion}` : ""} · {e.strength}{e.line ? ` · line ${e.line}` : ""}</p>
              {e.engine !== "clamav" && <p className="text-[12px] text-muted">{e.description}</p>}
            </li>
          ))}
        </ul>
        <p className="text-[12px] text-subtle">File contents are not shown here. To look at the file, use the website's File Manager (requires Manage websites; access is audited).</p>
      </div>
    </Dialog>
  );
}
